import { useState, useEffect, useRef, useMemo } from 'react';
import type { Company } from '@/types/company';
import { allBusinessTags, allSourceTags } from '@/types/company';
import { fetchUsers } from '@/api/users';
import { enrichCompanyInfo, type EnrichCompany } from '@/api/companies';
import { ApiError } from '@/api/client';
import type { User } from '@/types/user';

interface CompanyFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  company?: Company | null;
  onSubmit?: (company: Omit<Company, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onUpdate?: (id: string, company: Omit<Company, 'id' | 'createdAt' | 'updatedAt'>) => void;
}

interface ContactInput {
  name: string;
  phone: string;
  position: string;
}

interface DemandInput {
  description: string;
  source: string;
  sourceTag: string;
  date: string;
}

function getEmptyContacts(): ContactInput[] {
  return [{ name: '', phone: '', position: '' }];
}

function getEmptyDemands(): DemandInput[] {
  return [{ description: '', source: '', sourceTag: '', date: '' }];
}

export default function CompanyFormModal({ isOpen, onClose, company, onSubmit, onUpdate }: CompanyFormModalProps) {
  const isEdit = !!company;
  const prevCompanyRef = useRef<Company | null | undefined>(null);

  const [name, setName] = useState('');
  const [creditCode, setCreditCode] = useState('');
  const [address, setAddress] = useState('');
  const [website, setWebsite] = useState('');
  const [type, setType] = useState<'实体企业' | '认证机构' | '第三方咨询机构'>('实体企业');
  const [responsiblePerson, setResponsiblePerson] = useState('');
  const [selectedBusinessTags, setSelectedBusinessTags] = useState<string[]>([]);
  const [selectedSourceTags, setSelectedSourceTags] = useState<string[]>([]);
  const [contacts, setContacts] = useState<ContactInput[]>(getEmptyContacts());
  const [demands, setDemands] = useState<DemandInput[]>(getEmptyDemands());
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);

  // 从第三方数据源(天眼查/官方等)获取企业信息 — 仅新增模式可用。
  const [enriching, setEnriching] = useState(false);
  const [enrichBanner, setEnrichBanner] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);
  const enrichAbortRef = useRef<AbortController | null>(null);

  const filteredUsers = useMemo(() => {
    if (!userSearchQuery.trim()) return allUsers;
    const q = userSearchQuery.toLowerCase();
    return allUsers.filter(
      (u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q) || u.department.toLowerCase().includes(q),
    );
  }, [allUsers, userSearchQuery]);

  // Fetch users when dropdown opens
  useEffect(() => {
    if (userDropdownOpen && allUsers.length === 0 && !usersLoading) {
      setUsersLoading(true);
      fetchUsers()
        .then(setAllUsers)
        .catch(() => { /* silently fail */ })
        .finally(() => setUsersLoading(false));
    }
  }, [userDropdownOpen, allUsers.length, usersLoading]);

  useEffect(() => {
    if (!isOpen) return;
    if (prevCompanyRef.current === company) return;
    prevCompanyRef.current = company;

    if (company) {
      setName(company.name);
      setCreditCode(company.creditCode);
      setAddress(company.address);
      setWebsite(company.website);
      setType(company.type);
      setResponsiblePerson(company.responsiblePerson || '');
      setSelectedBusinessTags(company.businessTags);
      setSelectedSourceTags(company.sourceTags);
      setContacts(
        company.contacts.length > 0
          ? company.contacts.map((c) => ({ name: c.name, phone: c.phone, position: c.position }))
          : getEmptyContacts(),
      );
      setDemands(
        company.businessDemands.length > 0
          ? company.businessDemands.map((d) => ({ description: d.description, source: d.source, sourceTag: d.sourceTag, date: d.date }))
          : getEmptyDemands(),
      );
    } else {
      setName('');
      setCreditCode('');
      setAddress('');
      setWebsite('');
      setType('实体企业');
      setResponsiblePerson('');
      setSelectedBusinessTags([]);
      setSelectedSourceTags([]);
      setContacts(getEmptyContacts());
      setDemands(getEmptyDemands());
    }
    setErrors({});
    setEnrichBanner(null);
  }, [isOpen, company]);

  // modal 关闭或卸载时取消未完成的 enrichment 请求,避免 "setState on unmounted"。
  useEffect(() => {
    if (!isOpen && enrichAbortRef.current) {
      enrichAbortRef.current.abort();
      enrichAbortRef.current = null;
    }
  }, [isOpen]);

  useEffect(() => {
    return () => {
      enrichAbortRef.current?.abort();
    };
  }, []);

  if (!isOpen) return null;

  /**
   * 调用后端 enrich 接口,把返回的第三方数据回填到表单。
   * 当前后端 provider=mock,数据按 name 确定性生成,无外部网络调用。
   * 未来后端切换成真实数据源,本函数不需要修改。
   */
  const handleEnrichFromThirdParty = async () => {
    // 取消上一次未完成的请求
    enrichAbortRef.current?.abort();
    const ctrl = new AbortController();
    enrichAbortRef.current = ctrl;
    // 8s 超时,防止 mock 实现意外变成真实 provider 时长时间挂起
    const timeoutId = window.setTimeout(() => ctrl.abort(), 8000);

    setEnriching(true);
    setEnrichBanner(null);
    try {
      const trimmed = name.trim();
      if (!trimmed) {
        setEnrichBanner({ kind: 'error', text: '请先在"企业名称"输入框中输入企业名称' });
        return;
      }
      const data: EnrichCompany = await enrichCompanyInfo({
        name: trimmed,
        creditCode: creditCode.trim() || undefined,
        signal: ctrl.signal,
      });
      applyEnrichment(data);
      const src = data.source === 'mock' ? 'mock 数据源(占位)' : data.source || '第三方数据源';
      setEnrichBanner({
        kind: 'success',
        text: `已从 ${src} 获取信息,请核对后保存`,
      });
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') return;
      const msg =
        err instanceof ApiError && err.status === 501
          ? '当前数据源尚未接入,请手动填写或联系管理员'
          : err instanceof ApiError
            ? `获取失败: ${err.detail || err.message}`
            : err instanceof Error
              ? `获取失败: ${err.message}`
              : '获取失败,请稍后重试';
      setEnrichBanner({ kind: 'error', text: msg });
    } finally {
      window.clearTimeout(timeoutId);
      if (enrichAbortRef.current === ctrl) enrichAbortRef.current = null;
      setEnriching(false);
    }
  };

  /** 把第三方数据映射回表单字段。空字符串不覆盖用户已填写的字段。 */
  const applyEnrichment = (data: EnrichCompany) => {
    if (data.name) setName(data.name);
    if (data.creditCode) setCreditCode(data.creditCode);
    if (data.address) setAddress(data.address);
    if (data.website) setWebsite(data.website);
    if (data.legalRepresentative) setResponsiblePerson(data.legalRepresentative);
    // 经营范围 放在第一条业务需求描述里(避免直接覆盖用户已有输入)
    if (data.businessScope) {
      setDemands((prev) => {
        // 找一条空的优先填;否则追加新的一条
        const idx = prev.findIndex((d) => !d.description.trim());
        const next = [...prev];
        if (idx >= 0) {
          next[idx] = { ...next[idx], description: data.businessScope };
        } else {
          next.push({
            description: data.businessScope!,
            source: '第三方数据源',
            sourceTag: data.source,
            date: new Date().toISOString().split('T')[0],
          });
        }
        return next;
      });
    }
    // 类型映射:third party type 字段 → 内部枚举
    if (data.type === 'cert') setType('认证机构');
    else if (data.type === 'consult') setType('第三方咨询机构');
    else if (data.type === 'entity' || data.type === '') setType('实体企业');
  };

  const toggleBusinessTag = (tag: string) => {
    setSelectedBusinessTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  const toggleSourceTag = (tag: string) => {
    setSelectedSourceTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag],
    );
  };

  const updateContact = (index: number, field: keyof ContactInput, value: string) => {
    setContacts((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addContact = () => {
    setContacts((prev) => [...prev, { name: '', phone: '', position: '' }]);
  };

  const removeContact = (index: number) => {
    if (contacts.length <= 1) return;
    setContacts((prev) => prev.filter((_, i) => i !== index));
  };

  const updateDemand = (index: number, field: keyof DemandInput, value: string) => {
    setDemands((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addDemand = () => {
    setDemands((prev) => [...prev, { description: '', source: '', sourceTag: '', date: '' }]);
  };

  const removeDemand = (index: number) => {
    if (demands.length <= 1) return;
    setDemands((prev) => prev.filter((_, i) => i !== index));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!name.trim()) newErrors.name = '请输入企业名称';
    if (!creditCode.trim()) newErrors.creditCode = '请输入统一信用代码';
    if (!address.trim()) newErrors.address = '请输入地址';
    if (selectedBusinessTags.length === 0) newErrors.businessTags = '请至少选择一个业务标签';
    if (selectedSourceTags.length === 0) newErrors.sourceTags = '请至少选择一个来源标签';

    contacts.forEach((c, i) => {
      if (c.name.trim() && !c.phone.trim()) {
        newErrors[`contact_${i}_phone`] = '请输入联系电话';
      }
      if (!c.name.trim() && c.phone.trim()) {
        newErrors[`contact_${i}_name`] = '请输入联系人姓名';
      }
    });

    demands.forEach((d, i) => {
      if (d.description.trim() && !d.source.trim()) {
        newErrors[`demand_${i}_source`] = '请输入来源';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildCompanyData = (): Omit<Company, 'id' | 'createdAt' | 'updatedAt'> => {
    const prefix = isEdit ? 'edit_c' : 'new_c';
    const dPrefix = isEdit ? 'edit_d' : 'new_d';
    return {
      name: name.trim(),
      creditCode: creditCode.trim(),
      address: address.trim(),
      website: website.trim(),
      type,
      responsiblePerson: responsiblePerson.trim(),
      businessTags: selectedBusinessTags,
      sourceTags: selectedSourceTags,
      contacts: contacts
        .filter((c) => c.name.trim() || c.phone.trim())
        .map((c, i) => ({
          id: `${prefix}${Date.now()}_${i}`,
          name: c.name.trim(),
          phone: c.phone.trim(),
          position: c.position.trim(),
        })),
      businessDemands: demands
        .filter((d) => d.description.trim())
        .map((d, i) => ({
          id: `${dPrefix}${Date.now()}_${i}`,
          description: d.description.trim(),
          source: d.source.trim(),
          sourceTag: d.sourceTag.trim(),
          date: d.date || new Date().toISOString().split('T')[0],
        })),
    };
  };

  const handleSubmit = () => {
    if (!validate()) return;
    setSubmitting(true);

    const companyData = buildCompanyData();

    if (isEdit && onUpdate) {
      onUpdate(company!.id, companyData);
    } else if (onSubmit) {
      onSubmit(companyData);
    }

    setSubmitting(false);
    onClose();
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      onClick={handleClose}
    >
      <div className="absolute inset-0 bg-foreground-950/40" />
      <div
        className="relative bg-background-50 rounded-2xl w-full max-w-[720px] max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-background-50 border-b border-background-200/70 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isEdit ? 'bg-accent-100' : 'bg-primary-100'}`}>
              <i className={`text-lg ${isEdit ? 'ri-edit-line text-accent-600' : 'ri-add-line text-primary-600'}`}></i>
            </div>
            <div>
              <h2 className="text-lg font-heading font-bold text-foreground-900">
                {isEdit ? '编辑企业' : '新增企业'}
              </h2>
              <p className="text-xs text-foreground-500">
                {isEdit ? `正在编辑：${company!.name}` : '填写企业信息，构建客户画像'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-500 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6">
          {/* Section 1: 基本信息 */}
          <div>
            <div className="flex items-center justify-between gap-2 mb-4">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-primary-600">1</span>
                </div>
                <h3 className="text-sm font-heading font-semibold text-foreground-800">基本信息</h3>
              </div>
              {!isEdit && (
                <button
                  type="button"
                  onClick={handleEnrichFromThirdParty}
                  disabled={enriching}
                  title="根据企业名称从天眼查(当前为占位数据源)一键获取工商信息"
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-primary-200 text-primary-700 bg-primary-50 hover:bg-primary-100 hover:border-primary-300 disabled:opacity-60 disabled:cursor-not-allowed transition-colors cursor-pointer"
                >
                  {enriching ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-sm"></i>
                      获取中...
                    </>
                  ) : (
                    <>
                      <i className="ri-search-eye-line text-sm"></i>
                      从天眼查获取
                    </>
                  )}
                </button>
              )}
            </div>
            {enrichBanner && (
              <div
                role="status"
                className={`mb-3 flex items-start gap-2 px-3 py-2 rounded-lg text-xs ${
                  enrichBanner.kind === 'success'
                    ? 'bg-primary-50 text-primary-700 border border-primary-100'
                    : 'bg-red-50 text-red-700 border border-red-100'
                }`}
              >
                <i
                  className={`${
                    enrichBanner.kind === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'
                  } text-base leading-4 mt-px flex-shrink-0`}
                ></i>
                <span className="flex-1 break-words">{enrichBanner.text}</span>
                <button
                  type="button"
                  onClick={() => setEnrichBanner(null)}
                  className="opacity-60 hover:opacity-100 transition-opacity cursor-pointer"
                  aria-label="关闭提示"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1.5">
                  企业名称 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="请输入企业名称"
                  className={`w-full h-10 px-3 rounded-lg border text-sm bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 transition-all ${
                    errors.name
                      ? 'border-red-400 focus:ring-red-100'
                      : 'border-background-200 focus:border-primary-400 focus:ring-primary-100'
                  }`}
                />
                {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1.5">
                  统一信用代码 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={creditCode}
                  onChange={(e) => setCreditCode(e.target.value)}
                  placeholder="请输入统一信用代码"
                  className={`w-full h-10 px-3 rounded-lg border text-sm bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 transition-all ${
                    errors.creditCode
                      ? 'border-red-400 focus:ring-red-100'
                      : 'border-background-200 focus:border-primary-400 focus:ring-primary-100'
                  }`}
                />
                {errors.creditCode && <p className="text-xs text-red-500 mt-1">{errors.creditCode}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1.5">
                  地址 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="请输入企业地址"
                  className={`w-full h-10 px-3 rounded-lg border text-sm bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 transition-all ${
                    errors.address
                      ? 'border-red-400 focus:ring-red-100'
                      : 'border-background-200 focus:border-primary-400 focus:ring-primary-100'
                  }`}
                />
                {errors.address && <p className="text-xs text-red-500 mt-1">{errors.address}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1.5">网址</label>
                <input
                  type="text"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="如：www.example.com"
                  className="w-full h-10 px-3 rounded-lg border border-background-200 text-sm bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                />
              </div>
              <div className="relative">
                <label className="block text-xs font-medium text-foreground-600 mb-1.5">负责人</label>
                <button
                  type="button"
                  onClick={() => {
                    setUserDropdownOpen(!userDropdownOpen);
                    setUserSearchQuery('');
                  }}
                  className={`w-full h-10 px-3 rounded-lg border text-sm text-left flex items-center justify-between transition-all cursor-pointer ${
                    userDropdownOpen
                      ? 'border-primary-400 ring-2 ring-primary-100'
                      : 'border-background-200 hover:border-background-300'
                  } bg-background-50`}
                >
                  <span className={responsiblePerson ? 'text-foreground-800' : 'text-foreground-400'}>
                    {responsiblePerson || '搜索并选择负责人'}
                  </span>
                  <i className={`ri-arrow-down-s-line text-foreground-400 transition-transform ${userDropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {responsiblePerson && (
                  <button
                    type="button"
                    onClick={() => setResponsiblePerson('')}
                    className="absolute right-8 top-[30px] -translate-y-1/2 text-foreground-400 hover:text-red-500 transition-colors cursor-pointer"
                  >
                    <i className="ri-close-circle-fill text-sm"></i>
                  </button>
                )}
                {userDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setUserDropdownOpen(false)} />
                    <div className="absolute top-full mt-1 left-0 z-20 bg-background-50 rounded-xl border border-background-200/70 shadow-sm w-full py-2">
                      <div className="px-3 pb-2">
                        <div className="relative">
                          <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
                          <input
                            type="text"
                            value={userSearchQuery}
                            onChange={(e) => setUserSearchQuery(e.target.value)}
                            placeholder="搜索姓名、邮箱或部门..."
                            className="w-full h-8 pl-7 pr-3 rounded-lg border border-background-200 bg-background-50 text-xs text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
                            onClick={(e) => e.stopPropagation()}
                            autoFocus
                          />
                        </div>
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        {usersLoading && allUsers.length === 0 ? (
                          <p className="px-4 py-3 text-xs text-foreground-400 text-center">加载中...</p>
                        ) : filteredUsers.length === 0 ? (
                          <p className="px-4 py-3 text-xs text-foreground-400 text-center">未找到匹配的用户</p>
                        ) : (
                          filteredUsers.map((user) => (
                            <button
                              key={user.id}
                              type="button"
                              onClick={() => {
                                setResponsiblePerson(user.name);
                                setUserDropdownOpen(false);
                                setUserSearchQuery('');
                              }}
                              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-background-100 transition-colors cursor-pointer ${
                                responsiblePerson === user.name ? 'bg-primary-50' : ''
                              }`}
                            >
                              <div className="w-7 h-7 rounded-full bg-background-200 flex items-center justify-center flex-shrink-0">
                                <span className="text-[11px] font-medium text-foreground-600">{user.name.charAt(0)}</span>
                              </div>
                              <div className="min-w-0">
                                <p className="text-sm text-foreground-800">{user.name}</p>
                                <p className="text-[11px] text-foreground-500 truncate">{user.department} · {user.email}</p>
                              </div>
                              {responsiblePerson === user.name && (
                                <i className="ri-check-line text-primary-500 ml-auto flex-shrink-0"></i>
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-foreground-600 mb-1.5">
                  企业类型 <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-2">
                  {(['实体企业', '认证机构', '第三方咨询机构'] as const).map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setType(t)}
                      className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer border ${
                        type === t
                          ? 'bg-primary-500 text-background-50 border-primary-500'
                          : 'bg-background-50 text-foreground-600 border-background-200 hover:border-background-300'
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: 标签 */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-6 h-6 rounded bg-accent-100 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold text-accent-600">2</span>
              </div>
              <h3 className="text-sm font-heading font-semibold text-foreground-800">标签信息</h3>
            </div>

            {/* 业务标签 */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-foreground-600 mb-2">
                业务标签 <span className="text-red-500">*</span>
                {selectedBusinessTags.length > 0 && (
                  <span className="text-accent-600 ml-1">（已选 {selectedBusinessTags.length} 个）</span>
                )}
              </label>
              <div className="flex flex-wrap gap-1.5">
                {allBusinessTags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => toggleBusinessTag(tag)}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all cursor-pointer border ${
                      selectedBusinessTags.includes(tag)
                        ? 'bg-accent-500 text-background-50 border-accent-500'
                        : 'bg-background-50 text-foreground-600 border-background-200 hover:border-accent-300 hover:text-accent-600'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
              {errors.businessTags && <p className="text-xs text-red-500 mt-1">{errors.businessTags}</p>}
            </div>

            {/* 来源标签 */}
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-2">
                来源标签 <span className="text-red-500">*</span>
                {selectedSourceTags.length > 0 && (
                  <span className="text-secondary-600 ml-1">（已选 {selectedSourceTags.length} 个）</span>
                )}
              </label>
              <div className="flex flex-wrap gap-1.5">
                {allSourceTags.map((tag) => (
                  <button
                    key={tag}
                    type="button"
                    onClick={() => toggleSourceTag(tag)}
                    className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all cursor-pointer border ${
                      selectedSourceTags.includes(tag)
                        ? 'bg-secondary-500 text-background-50 border-secondary-500'
                        : 'bg-background-50 text-foreground-600 border-background-200 hover:border-secondary-300 hover:text-secondary-600'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
              {errors.sourceTags && <p className="text-xs text-red-500 mt-1">{errors.sourceTags}</p>}
            </div>
          </div>

          {/* Section 3: 联系人 */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded bg-secondary-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-secondary-600">3</span>
                </div>
                <h3 className="text-sm font-heading font-semibold text-foreground-800">联系人</h3>
              </div>
              <button
                type="button"
                onClick={addContact}
                className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-md transition-colors cursor-pointer"
              >
                <i className="ri-add-line text-sm"></i>
                添加
              </button>
            </div>
            <div className="space-y-3">
              {contacts.map((contact, index) => (
                <div key={index} className="flex items-start gap-2">
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div>
                      <input
                        type="text"
                        value={contact.name}
                        onChange={(e) => updateContact(index, 'name', e.target.value)}
                        placeholder="姓名"
                        className={`w-full h-10 px-3 rounded-lg border text-sm bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all ${
                          errors[`contact_${index}_name`] ? 'border-red-400 focus:ring-red-100' : 'border-background-200'
                        }`}
                      />
                      {errors[`contact_${index}_name`] && (
                        <p className="text-xs text-red-500 mt-0.5">{errors[`contact_${index}_name`]}</p>
                      )}
                    </div>
                    <div>
                      <input
                        type="text"
                        value={contact.phone}
                        onChange={(e) => updateContact(index, 'phone', e.target.value)}
                        placeholder="电话"
                        className={`w-full h-10 px-3 rounded-lg border text-sm bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all ${
                          errors[`contact_${index}_phone`] ? 'border-red-400 focus:ring-red-100' : 'border-background-200'
                        }`}
                      />
                      {errors[`contact_${index}_phone`] && (
                        <p className="text-xs text-red-500 mt-0.5">{errors[`contact_${index}_phone`]}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={contact.position}
                        onChange={(e) => updateContact(index, 'position', e.target.value)}
                        placeholder="职位"
                        className="w-full h-10 px-3 rounded-lg border border-background-200 text-sm bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      />
                      {contacts.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeContact(index)}
                          className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-foreground-400 hover:text-red-500 transition-colors cursor-pointer flex-shrink-0"
                        >
                          <i className="ri-delete-bin-line text-sm"></i>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Section 4: 业务需求历史 */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded bg-background-200 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-foreground-600">4</span>
                </div>
                <h3 className="text-sm font-heading font-semibold text-foreground-800">业务需求历史</h3>
                <span className="text-xs text-foreground-500">（选填）</span>
              </div>
              <button
                type="button"
                onClick={addDemand}
                className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-md transition-colors cursor-pointer"
              >
                <i className="ri-add-line text-sm"></i>
                添加
              </button>
            </div>
            <div className="space-y-3">
              {demands.map((demand, index) => (
                <div key={index} className="bg-background-100 rounded-xl p-4">
                  <div className="flex items-start justify-between mb-2">
                    <span className="text-xs text-foreground-500 font-medium">需求 #{index + 1}</span>
                    {demands.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeDemand(index)}
                        className="text-xs text-foreground-400 hover:text-red-500 transition-colors cursor-pointer"
                      >
                        <i className="ri-close-line"></i> 移除
                      </button>
                    )}
                  </div>
                  <div className="space-y-2">
                    <div>
                      <input
                        type="text"
                        value={demand.description}
                        onChange={(e) => updateDemand(index, 'description', e.target.value)}
                        placeholder="需求描述"
                        className="w-full h-10 px-3 rounded-lg border border-background-200 text-sm bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <input
                        type="text"
                        value={demand.source}
                        onChange={(e) => updateDemand(index, 'source', e.target.value)}
                        placeholder="信息来源"
                        className={`w-full h-10 px-3 rounded-lg border text-sm bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all ${
                          errors[`demand_${index}_source`] ? 'border-red-400 focus:ring-red-100' : 'border-background-200'
                        }`}
                      />
                      <input
                        type="text"
                        value={demand.sourceTag}
                        onChange={(e) => updateDemand(index, 'sourceTag', e.target.value)}
                        placeholder="来源标签"
                        className="w-full h-10 px-3 rounded-lg border border-background-200 text-sm bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      />
                      <input
                        type="date"
                        value={demand.date}
                        onChange={(e) => updateDemand(index, 'date', e.target.value)}
                        className="w-full h-10 px-3 rounded-lg border border-background-200 text-sm bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-background-50 border-t border-background-200/70 px-6 py-4 flex items-center justify-end gap-3">
          <button
            onClick={handleClose}
            className="px-4 py-2.5 rounded-lg text-sm font-medium text-foreground-600 hover:text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex items-center gap-2 px-5 py-2.5 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-400 text-background-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            {submitting ? (
              <>
                <i className="ri-loader-4-line animate-spin text-base"></i>
                保存中...
              </>
            ) : (
              <>
                <i className="ri-check-line text-base"></i>
                {isEdit ? '保存修改' : '保存企业'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}